import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material'
import {
  TRACK_EVENTS,
  EventCategory,
  EVENTS_BY_CATEGORY,
  CATEGORY_LABELS,
} from '@tnf-recruit/shared'
import type { AthleteProfileFormApi } from '../../../hooks/useAthleteProfileForm'

/** Athletic info + primary events + PR marks, shared by the onboarding wizard and the edit form. */
export const AthleticInfoFields = ({ form }: { form: AthleteProfileFormApi }) => {
  const { data, fieldErrors, setData, setFieldErrors, validateAthleticField, prMarks, setPrMarks } =
    form

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="NCAA ID (Optional)"
          value={data.athleticInfo.ncaaId}
          onChange={e =>
            setData({
              ...data,
              athleticInfo: { ...data.athleticInfo, ncaaId: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Position (Optional)"
          value={data.athleticInfo.position}
          onChange={e =>
            setData({
              ...data,
              athleticInfo: { ...data.athleticInfo, position: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required error={!!fieldErrors['athleticInfo.height.feet']}>
          <InputLabel>Height (Feet)</InputLabel>
          <Select
            value={data.athleticInfo.height.feet}
            onChange={e => {
              const nextHeight = { ...data.athleticInfo.height, feet: Number(e.target.value) }
              const next = { ...data.athleticInfo, height: nextHeight }
              setData({ ...data, athleticInfo: next })
              validateAthleticField('height.feet', 'athleticInfo.height.feet', next)
            }}
            onBlur={() =>
              validateAthleticField('height.feet', 'athleticInfo.height.feet', data.athleticInfo)
            }
          >
            {[3, 4, 5, 6, 7, 8].map(ft => (
              <MenuItem key={ft} value={ft}>
                {ft} ft
              </MenuItem>
            ))}
          </Select>
          {fieldErrors['athleticInfo.height.feet'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
              {fieldErrors['athleticInfo.height.feet']}
            </Typography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required error={!!fieldErrors['athleticInfo.height.inches']}>
          <InputLabel>Height (Inches)</InputLabel>
          <Select
            value={data.athleticInfo.height.inches}
            onChange={e => {
              const nextHeight = { ...data.athleticInfo.height, inches: Number(e.target.value) }
              const next = { ...data.athleticInfo, height: nextHeight }
              setData({ ...data, athleticInfo: next })
              validateAthleticField('height.inches', 'athleticInfo.height.inches', next)
            }}
            onBlur={() =>
              validateAthleticField(
                'height.inches',
                'athleticInfo.height.inches',
                data.athleticInfo
              )
            }
          >
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(inch => (
              <MenuItem key={inch} value={inch}>
                {inch} in
              </MenuItem>
            ))}
          </Select>
          {fieldErrors['athleticInfo.height.inches'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
              {fieldErrors['athleticInfo.height.inches']}
            </Typography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl
          fullWidth
          required
          error={!!fieldErrors['athleticInfo.plannedFirstYearOfCollege']}
        >
          <InputLabel>Planned first year of college</InputLabel>
          <Select
            value={data.athleticInfo.plannedFirstYearOfCollege}
            label="Planned first year of college"
            onChange={e => {
              const next = {
                ...data.athleticInfo,
                plannedFirstYearOfCollege: e.target.value as string,
              }
              setData({ ...data, athleticInfo: next })
              validateAthleticField(
                'plannedFirstYearOfCollege',
                'athleticInfo.plannedFirstYearOfCollege',
                next
              )
            }}
            onBlur={() =>
              validateAthleticField(
                'plannedFirstYearOfCollege',
                'athleticInfo.plannedFirstYearOfCollege',
                data.athleticInfo
              )
            }
          >
            <MenuItem value="">
              <em>Select a year</em>
            </MenuItem>
            {form.schoolYearOptions.map(y => (
              <MenuItem key={`college-${y}`} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors['athleticInfo.plannedFirstYearOfCollege'] && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 1.75 }}>
              {fieldErrors['athleticInfo.plannedFirstYearOfCollege']}
            </Typography>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Primary Event(s)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select the events you compete in
        </Typography>
        {Object.entries(EVENTS_BY_CATEGORY).map(([category, events]) => (
          <Box key={category} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {CATEGORY_LABELS[category as EventCategory]}
            </Typography>
            <Box display="flex" gap={1} flexWrap="wrap" mb={1}>
              {events.map(event => (
                <Chip
                  key={event.id}
                  label={event.name}
                  color={data.athleticInfo.primaryEvents.includes(event.id) ? 'primary' : 'default'}
                  onClick={() => {
                    const curr = data.athleticInfo.primaryEvents
                    const next = curr.includes(event.id)
                      ? curr.filter(id => id !== event.id)
                      : [...curr, event.id]
                    const nextAthletic = { ...data.athleticInfo, primaryEvents: next }
                    setData({ ...data, athleticInfo: nextAthletic })
                    validateAthleticField(
                      'primaryEvents',
                      'athleticInfo.primaryEvents',
                      nextAthletic
                    )
                  }}
                />
              ))}
            </Box>
          </Box>
        ))}
        {fieldErrors['athleticInfo.primaryEvents'] && (
          <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
            {fieldErrors['athleticInfo.primaryEvents']}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Personal Records (PRs)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each primary event you select must have a best mark (PR) before you can save.
        </Typography>
        {data.athleticInfo.primaryEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
            Select your primary events above to add personal records.
          </Typography>
        ) : (
          data.athleticInfo.primaryEvents.map(eventId => {
            const event = TRACK_EVENTS.find(e => e.id === eventId)
            if (!event) return null
            return (
              <Box key={eventId} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  required
                  label={`${event.name} — Best Mark (PR)`}
                  placeholder={event.isFieldEvent ? 'e.g., 6\'2"' : 'e.g., 10.45'}
                  error={!!fieldErrors[`prMark.${eventId}`]}
                  helperText={
                    fieldErrors[`prMark.${eventId}`] ??
                    (event.isFieldEvent ? 'Distance or height' : 'Time in seconds')
                  }
                  value={prMarks[eventId] ?? ''}
                  onChange={e => {
                    const v = e.target.value
                    setPrMarks(prev => ({ ...prev, [eventId]: v }))
                    setFieldErrors(prev => {
                      if (!prev[`prMark.${eventId}`] && !prev.personalRecords) return prev
                      const next = { ...prev }
                      delete next[`prMark.${eventId}`]
                      if (Object.keys(next).filter(k => k.startsWith('prMark.')).length === 0) {
                        delete next.personalRecords
                      }
                      return next
                    })
                  }}
                />
              </Box>
            )
          })
        )}
        {fieldErrors.personalRecords && (
          <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>
            {fieldErrors.personalRecords}
          </Typography>
        )}
        <Divider sx={{ my: 1 }} />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Recruiting Video Link (Optional)"
          placeholder="https://youtube.com/watch?v=..."
          value={data.athleticInfo.recruitingVideoLink}
          helperText="Add a link to your best performances"
          onChange={e =>
            setData({
              ...data,
              athleticInfo: { ...data.athleticInfo, recruitingVideoLink: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Club Team (Optional)"
          value={data.athleticInfo.clubTeam}
          onChange={e =>
            setData({
              ...data,
              athleticInfo: { ...data.athleticInfo, clubTeam: e.target.value },
            })
          }
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Club Position (Optional)"
          value={data.athleticInfo.clubPosition}
          onChange={e =>
            setData({
              ...data,
              athleticInfo: { ...data.athleticInfo, clubPosition: e.target.value },
            })
          }
        />
      </Grid>
    </Grid>
  )
}
